import React, { useEffect, useRef, useState, useCallback } from 'react';
import { adminApi } from '../../api';
import { toast } from '../../hooks/use-toast';
import { MessageSquare, Send, Shield, User, Search, X, Image as ImageIcon } from 'lucide-react';

const SupportAdmin = () => {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState('');
  const [lightbox, setLightbox] = useState(null);
  const scrollRef = useRef(null);

  const loadThreads = useCallback(async () => {
    try {
      const t = await adminApi.listThreads();
      setThreads(t);
      if (!selected && t.length > 0) setSelected(t[0]);
    } catch (e) { /* noop */ }
  }, [selected]);

  const loadMessages = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const m = await adminApi.getThread(userId);
      setMessages(m);
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => { loadThreads(); }, [loadThreads]);

  useEffect(() => {
    const iv = setInterval(loadThreads, 5000);
    return () => clearInterval(iv);
  }, [loadThreads]);

  useEffect(() => {
    if (selected?.user_id) loadMessages(selected.user_id);
  }, [selected, loadMessages]);

  useEffect(() => {
    if (!selected?.user_id) return;
    const iv = setInterval(() => loadMessages(selected.user_id), 4000);
    return () => clearInterval(iv);
  }, [selected, loadMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const val = text.trim();
    if (!val || !selected || sending) return;
    setSending(true);
    const tmp = { id: 'tmp-' + Date.now(), from: 'admin', text: val, created_at: 'sending…', _tmp: true };
    setMessages((m) => [...m, tmp]);
    setText('');
    try {
      await adminApi.replyThread(selected.user_id, val);
      await Promise.all([loadMessages(selected.user_id), loadThreads()]);
    } catch (e) {
      setMessages((m) => m.filter((x) => x.id !== tmp.id));
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Try again' });
    } finally { setSending(false); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const filtered = threads.filter((t) => !q.trim() || (t.username || '').toLowerCase().includes(q.toLowerCase()) || (t.display_name || '').toLowerCase().includes(q.toLowerCase()));
  const totalUnread = threads.reduce((s, t) => s + (t.unread || 0), 0);

  return (
    <div className="px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-[#5a5a5a] font-mono">SUPPORT</div>
          <div className="text-2xl text-[#e6e6e6] font-mono">
            {threads.length} <span className="text-[10px] text-[#5a5a5a] tracking-[0.25em]">CONVERSATIONS</span>
            {totalUnread > 0 && <span className="ml-3 text-[11px] px-2 py-0.5 rounded-full bg-[#22c55e] text-black font-semibold">{totalUnread} UNREAD</span>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-[320px_1fr] gap-4 h-[calc(100vh-190px)] min-h-[540px]">
        {/* Threads list */}
        <div className="border border-[#151515] rounded-md bg-[#0a0a0a] flex flex-col overflow-hidden font-mono">
          <div className="p-3 border-b border-[#151515]">
            <div className="flex items-center gap-2 border border-[#1f1f1f] rounded-md px-2 py-1.5 focus-within:border-[#22c55e]">
              <Search size={12} className="text-[#5a5a5a]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search user…" className="flex-1 bg-transparent text-[12px] text-[#e6e6e6] outline-none" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 && (
              <div className="p-6 text-center text-[12px] text-[#5a5a5a]">No conversations yet.</div>
            )}
            {filtered.map((t) => {
              const active = selected?.user_id === t.user_id;
              return (
                <button
                  key={t.user_id}
                  onClick={() => setSelected(t)}
                  className={`w-full text-left px-3 py-3 border-b border-[#0f0f0f] last:border-0 hover:bg-[#0d0d0d] transition-colors ${active ? 'bg-[#0d1a11] border-l-2 border-l-[#22c55e]' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center border border-[#1f1f1f] text-[#8a8a8a] shrink-0">
                        <User size={12} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[12px] text-[#e6e6e6] truncate">@{t.username || '-'}</div>
                        <div className="text-[10px] text-[#5a5a5a] truncate">{t.display_name || t.user_id}</div>
                      </div>
                    </div>
                    {t.unread > 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#22c55e] text-black font-semibold min-w-[18px] text-center">{t.unread}</span>
                    )}
                  </div>
                  <div className="mt-1.5 text-[11px] text-[#7a7a7a] truncate">
                    <span className={`mr-1 ${t.last_from === 'admin' ? 'text-[#22c55e]' : 'text-[#5a5a5a]'}`}>
                      {t.last_from === 'admin' ? '↩' : '›'}
                    </span>
                    {t.last_text}
                  </div>
                  <div className="text-[9px] text-[#4a4a4a] mt-0.5">{t.last_at}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Conversation */}
        <div className="border border-[#151515] rounded-md bg-[#0a0a0a] flex flex-col overflow-hidden font-mono">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center text-[#5a5a5a] text-[12px] tracking-[0.2em]">
              <MessageSquare size={20} className="mr-2" /> SELECT A CONVERSATION
            </div>
          ) : (
            <>
              <div className="px-4 py-3 border-b border-[#151515] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center border border-[#22c55e]/40 text-[#22c55e] bg-[#0f1a12]">
                    <User size={13} />
                  </div>
                  <div>
                    <div className="text-[13px] text-[#e6e6e6]">@{selected.username || '-'}</div>
                    <div className="text-[10px] text-[#7a7a7a]">{selected.display_name} · <code className="text-[#5a5a5a]">{selected.user_id}</code></div>
                  </div>
                </div>
                <span className="text-[10px] tracking-[0.2em] text-[#5a5a5a]">{messages.length} MESSAGES</span>
              </div>

              <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center text-[12px] text-[#5a5a5a] py-16">No messages in this thread yet.</div>
                )}
                {messages.map((m) => {
                  const mine = m.from === 'admin';
                  return (
                    <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[75%] flex gap-2 ${mine ? 'flex-row-reverse' : 'flex-row'} items-end`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center border shrink-0 ${mine ? 'border-[#22c55e]/40 text-[#22c55e] bg-[#0f1a12]' : 'border-[#1f1f1f] text-[#8a8a8a]'}`}>
                          {mine ? <Shield size={12} /> : <User size={12} />}
                        </div>
                        <div className="min-w-0">
                          <div className={`rounded-md overflow-hidden ${
                            mine ? 'bg-[#0d1a11] border border-[#1f3a24]' : 'bg-[#0d0d0d] border border-[#1f1f1f]'
                          }`}>
                            {m.image && (
                              <button
                                type="button"
                                onClick={() => setLightbox(m.image)}
                                className="block w-full max-w-[320px]"
                              >
                                <img src={m.image} alt="attachment" className="w-full h-auto max-h-[280px] object-cover" />
                              </button>
                            )}
                            {m.text && (
                              <div className="px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap break-words text-[#e6e6e6]">
                                {m.text}
                              </div>
                            )}
                          </div>
                          <div className={`text-[10px] text-[#5a5a5a] mt-0.5 flex items-center gap-1 ${mine ? 'justify-end' : 'justify-start'}`}>
                            {m.image && <ImageIcon size={9} className="text-[#22c55e]" />}
                            {m.created_at}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="border-t border-[#151515] p-3">
                <div className="flex items-end gap-2">
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={2}
                    placeholder="Type reply… (also sends to user's Telegram if configured)"
                    className="flex-1 bg-transparent border border-[#1f1f1f] rounded-md px-3 py-2 text-sm text-[#e6e6e6] outline-none focus:border-[#22c55e] resize-none"
                  />
                  <button
                    onClick={send}
                    disabled={sending || !text.trim()}
                    className="px-4 py-2.5 rounded-md text-[11px] tracking-[0.25em] bg-[#166534] hover:bg-[#15803d] disabled:opacity-50 text-[#e7ffef] flex items-center gap-2"
                  >
                    <Send size={12} /> SEND
                  </button>
                </div>
                <p className="text-[10px] text-[#5a5a5a] mt-2">Tip: You can also reply directly from Telegram — replies auto-sync here.</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4 py-6" onClick={() => setLightbox(null)}>
          <div className="absolute inset-0 bg-black/90" />
          <button className="absolute top-4 right-4 z-10 bg-black/70 rounded-full p-2 text-[#e6e6e6] hover:text-[#ef4444]">
            <X size={18} />
          </button>
          <img src={lightbox} alt="full" className="relative max-w-full max-h-full rounded-md" />
        </div>
      )}
    </div>
  );
};

export default SupportAdmin;
